import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { LogOut, ShieldCheck, User, ChevronDown, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import NotificationBell from "@/components/NotificationBell";
import useStandalone from "@/hooks/useStandalone";
const CHAMP = [
  { to: "/campionato/classifica-generale", label: "Classifica Generale" },
  { to: "/campionato/classifica-gironi", label: "Classifica Gironi" },
  { to: "/calendario", label: "Calendario" },
];
const COPPA = [
  { to: "/coppa/fase-a-girone", label: "Fase a Girone" },
  { to: "/coppa/playoff", label: "Playoff Champions" },
  { to: "/coppa/champions", label: "Champions League" },
  { to: "/coppa/europa", label: "Europa League" },
];

function NavDropdown({ label, items, testid }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={testid}
          className="inline-flex items-center gap-1.5 px-4 h-11 rounded-full text-base font-semibold text-slate-800 hover:text-slate-900 hover:bg-slate-100 transition-colors"
        >
          {label} <ChevronDown className="w-4 h-4 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="bg-white border-slate-200 text-slate-900">
        <DropdownMenuLabel className="text-slate-500 text-[10px] uppercase tracking-widest">{label}</DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-slate-100" />
        {items.map((it) => (
          <DropdownMenuItem key={it.to} asChild className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">
            <Link to={it.to} data-testid={`nav-${it.to.replaceAll("/", "-")}`}>{it.label}</Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isStandalone = useStandalone();

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b border-slate-200/60 bg-white/90${isStandalone ? " is-standalone" : ""}`}
              style={{ paddingTop: isStandalone
                ? "calc(env(safe-area-inset-top, 0px) + 14px)"
                : "env(safe-area-inset-top, 0px)" }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 h-16 sm:h-20 md:h-24 flex items-center justify-between gap-2 md:gap-3">
          {/* Logo — using the new 1:1 square asset (512x512) so aspect-square is clean */}
          <Link to="/" className="flex items-center shrink-0 group" data-testid="brand-home-link" aria-label="Home FIFE">
            <img src="/logo-fife-512.png" alt="Fantacalcio FIFE" width="96" height="96"
                 className="h-11 w-11 sm:h-14 sm:w-14 md:h-16 md:w-16 xl:h-20 xl:w-20 object-contain aspect-square transition-transform duration-500 group-hover:scale-105 group-hover:rotate-3" />
        </Link>

        {/* Desktop nav — visible only ≥ lg (1024px+); logged-in usa hamburger sotto 1280px per fare spazio all'avatar user */}
        <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center min-w-0 overflow-x-hidden">
          <NavDropdown label="Campionato" items={CHAMP} testid="nav-campionato" />
          <NavDropdown label="Coppa" items={COPPA} testid="nav-coppa" />
          <Link to="/squadre" className="inline-flex items-center gap-1 px-2 xl:px-3 h-10 rounded-full text-[13px] xl:text-[15px] font-semibold text-slate-800 hover:text-slate-900 hover:bg-slate-100 transition-colors whitespace-nowrap"
                data-testid="nav-squadre">Squadre</Link>
          <Link to="/fantascommesse" className="inline-flex items-center gap-1 px-2 xl:px-3 h-10 rounded-full text-[13px] xl:text-[15px] font-semibold text-slate-800 hover:text-slate-900 hover:bg-slate-100 transition-colors whitespace-nowrap"
                data-testid="nav-fantascommesse">Fantascommesse</Link>
          <Link to="/news" className="inline-flex items-center gap-1 px-2 xl:px-3 h-10 rounded-full text-[13px] xl:text-[15px] font-semibold text-slate-800 hover:text-slate-900 hover:bg-slate-100 transition-colors whitespace-nowrap"
                data-testid="nav-news">News</Link>
          <Link to="/aste" className="inline-flex items-center gap-1 px-2 xl:px-3 h-10 rounded-full text-[13px] xl:text-[15px] font-semibold text-slate-800 hover:text-slate-900 hover:bg-slate-100 transition-colors whitespace-nowrap"
                data-testid="nav-aste">Aste</Link>
          <Link to="/performance" className="inline-flex items-center gap-1 px-2 xl:px-3 h-10 rounded-full text-[13px] xl:text-[15px] font-semibold text-slate-800 hover:text-slate-900 hover:bg-slate-100 transition-colors whitespace-nowrap"
                data-testid="nav-performance">Calciatori</Link>
          <Link to="/albo-doro" className="hidden xl:inline-flex items-center gap-1 px-2 xl:px-3 h-10 rounded-full text-[13px] xl:text-[15px] font-semibold text-slate-800 hover:text-slate-900 hover:bg-slate-100 transition-colors whitespace-nowrap"
                data-testid="nav-albo-doro">Albo d'Oro</Link>
          <Link to="/confronto" className="hidden xl:inline-flex items-center gap-1 px-2 xl:px-3 h-10 rounded-full text-[13px] xl:text-[15px] font-semibold text-slate-800 hover:text-slate-900 hover:bg-slate-100 transition-colors whitespace-nowrap"
                data-testid="nav-confronto">Confronta</Link>
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          {user && user.id && <NotificationBell />}
          {user && user.id ? (
            <>
              {/* Avatar dropdown compatto — sostituisce Admin/Area/Logout separati */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="hidden lg:inline-flex items-center gap-1.5 h-10 pl-1 pr-2 rounded-full border border-slate-200 hover:bg-slate-100 transition-colors"
                          data-testid="header-user-menu"
                          aria-label="Menu utente">
                    <span className="w-7 h-7 rounded-full bg-[var(--brand)] text-white text-xs font-black flex items-center justify-center" aria-hidden="true">
                      {(user.full_name || user.email || "?").trim().charAt(0).toUpperCase()}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white border-slate-200 text-slate-900 w-56">
                  <DropdownMenuLabel className="text-slate-500 text-[10px] uppercase tracking-widest">
                    {user.full_name || user.email}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-slate-100" />
                  <DropdownMenuItem asChild className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">
                    <Link to="/dashboard" data-testid="menu-area">
                      <User className="w-4 h-4 mr-2" /> Area riservata
                    </Link>
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem asChild className="cursor-pointer focus:bg-slate-100 focus:text-slate-900">
                      <Link to="/admin" data-testid="menu-admin">
                        <ShieldCheck className="w-4 h-4 mr-2" /> Pannello Admin
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-slate-100" />
                  <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }}
                                    className="cursor-pointer focus:bg-slate-100 focus:text-slate-900 text-red-600 focus:text-red-700"
                                    data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Esci
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => nav("/login")} data-testid="header-login-btn"
                      className={`${isStandalone ? "inline-flex" : "hidden md:inline-flex"} text-slate-900 hover:bg-slate-100 h-10`}>Accedi</Button>
              {!isStandalone && (
                <Button onClick={() => nav("/register")} data-testid="header-register-btn"
                        className="btn-brand rounded-full h-10">Registrati</Button>
              )}
            </>
          )}
          {/* Mobile menu — visible below lg */}
          <Sheet>
            <SheetTrigger asChild>
              <button className="lg:hidden p-2 rounded-full hover:bg-slate-100 pressable" aria-label="Menù" data-testid="mobile-menu-trigger">
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white border-slate-200 text-slate-900 w-80 overflow-y-auto"
                          style={{ paddingTop: "calc(1.5rem + env(safe-area-inset-top, 0px))" }}>
              <SheetHeader>
                <SheetTitle className="display font-black text-2xl text-slate-900">
                  Fantacalcio <span style={{ color: "var(--brand)" }}>FIFE</span>
                </SheetTitle>
              </SheetHeader>
              <div className="mt-6 space-y-4">
                <MobileGroup label="Campionato" items={CHAMP} />
                <MobileGroup label="Coppa" items={COPPA} />
                <Link to="/squadre" className="block text-lg py-2" data-testid="mobile-nav-squadre">Squadre</Link>
                <Link to="/fantascommesse" className="block text-lg py-2" data-testid="mobile-nav-fantascommesse">Fantascommesse</Link>
                <Link to="/news" className="block text-lg py-2" data-testid="mobile-nav-news">News</Link>
                <Link to="/aste" className="block text-lg py-2" data-testid="mobile-nav-aste">Aste</Link>
                <Link to="/performance" className="block text-lg py-2" data-testid="mobile-nav-performance">Calciatori</Link>
                <Link to="/albo-doro" className="block text-lg py-2" data-testid="mobile-nav-albo-doro">Albo d'Oro</Link>
                <Link to="/confronto" className="block text-lg py-2" data-testid="mobile-nav-confronto">Confronta Squadre</Link>
                <hr className="border-slate-200" />
                {user && user.id ? (
                  <>
                    {user.role === "admin" && <Link to="/admin" className="block py-2">Admin</Link>}
                    <Link to="/dashboard" className="block py-2">Area riservata</Link>
                    <button onClick={async () => { await logout(); nav("/"); }} className="block py-2 text-left w-full">Esci</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="block py-2">Accedi</Link>
                    {!isStandalone && (
                      <Link to="/register" className="block py-2" style={{ color: "var(--brand)" }}>Registrati</Link>
                    )}
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
    {/* Spacer: total header height = safe-area-inset-top + inline height.
        We build the height with inline calc() so it doesn't get compressed by the
        Tailwind border-box `h-*` utilities (padding would otherwise eat into the height). */}
    <div className="header-spacer" aria-hidden="true" />
    </>
  );
}

function MobileGroup({ label, items }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      {items.map((it) => (
        <Link key={it.to} to={it.to} className="block py-1.5 text-slate-700 hover:text-slate-900">{it.label}</Link>
      ))}
    </div>
  );
}
